// Google Analytics (GA4) 埋点封装。统计 ID 来自环境变量 VITE_GA_ID（appConfig.analytics.gaId）。
// 设计目标：可复用——路由用 trackPageView，组件用 useAnalytics() 拿到 track 后传 label + 数据。
// 没配 ID 时全部为 no-op：本地开发不加载 gtag、不发任何事件，避免无谓的外部请求。
import { appConfig } from '@/shared/config/env';

const GA_ID = appConfig.analytics.gaId;

let loaded = false;

// 标准 gtag 垫片：必须 push arguments 对象本身。
// gtag.js 只识别 arguments 形态的命令，push 普通数组不会被处理（会导致 config/event 全部静默丢弃）。
// 因此这里必须用普通函数 + arguments，不能用箭头函数 + rest 参数。
function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

// 在应用启动时调用一次：注入 gtag.js 并初始化。无 ID 直接跳过。
export const initAnalytics = () => {
  if (loaded || !GA_ID || typeof document === 'undefined') return;
  loaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.gtag = gtag;
  gtag('js', new Date());
  // SPA 由路由变化时手动发 page_view，这里关掉自动首屏统计避免重复计数。
  gtag('config', GA_ID, { send_page_view: false });
};

// 页面浏览埋点（路由用）。
export const trackPageView = (path, title) => {
  if (!GA_ID) return;
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.origin + path,
  });
};

// 登录态同步给 GA：user_id 用库里的 uuid（伪匿名标识，GA 政策允许；勿传邮箱/手机号等 PII），
// is_logged_in 作为用户属性，报表里可按登录用户/纯访客切分（分享页流量即可被过滤）。
// 传 null 表示匿名：显式清掉 user_id 并标记 is_logged_in=false。
// 时序：受保护路由的守卫会先 await 登录态再发首个 page_view，故 user_id 能带上；
// 公开页（分享页等）首个 page_view 可能先于登录态探测发出，属可接受误差。
export const setAnalyticsUser = (userId) => {
  if (!GA_ID) return;
  gtag('set', 'user_properties', { is_logged_in: userId ? 'true' : 'false' });
  gtag('config', GA_ID, { user_id: userId ?? null, send_page_view: false });
};

// 通用事件埋点：label = 事件名，data = 附加埋点数据。
export const track = (label, data = {}) => {
  if (!GA_ID) return;
  gtag('event', label, data);
};

// 组件里复用：const { track } = useAnalytics()。
export const useAnalytics = () => ({ track });
